/**
 * agentCommandCenter.ts — Studio Command Center display data.
 *
 * SOURCE OF TRUTH: frontend/studio/workspaces/*.md (passport files)
 *
 * This file is a DISPLAY CACHE — lightweight enough to render cards.
 * Keep fields in sync with the matching passport markdown file.
 *
 * UPDATE PROTOCOL (every workspace, every session):
 *   1. Complete meaningful work in the workspace.
 *   2. Update the markdown passport at passportPath.
 *   3. Update the matching fields here.
 *   4. Commit both files together.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type HealthStatus    = 'healthy' | 'degraded' | 'down';
export type KnowledgeStatus = 'in_sync' | 'needs_sync' | 'outdated';
export type Priority        = 'critical' | 'high' | 'medium' | 'low';

export interface WorkspaceOwnership {
  reactUI:        boolean;   // src/screens, src/components (non-Studio-OS)
  backend:        boolean;   // backend/ — API routes, migrations, database
  gameplay:       boolean;   // Phaser engine, game logic, sound
  studioOs:       boolean;   // /studio route, AgentCommandCenter, agentCommandCenter.ts
  infrastructure: boolean;   // auth, Firebase config, CI/CD, hosting, security rules
  passportSystem: boolean;   // studio/workspaces/ architecture and format standard
}

export interface ProductionBanner {
  branch:     string;
  health:     HealthStatus;
  commit:     string;
  lastDeploy: string;
}

export interface WorkspaceCurrentState {
  health:           HealthStatus;
  knowledgeStatus:  KnowledgeStatus;
  lastSync:         string;
  currentMission:   string;
  priority:         Priority;
  lastWin:          string;
  nextAction:       string;
  blockers:         string[];
  pendingDecisions: string[];
  branch:            string;
  constraints:       string[];
  extraVerification: string[];
  notes?:            string;
  lastUpdated:      string;
}

export interface Workspace {
  id:           string;
  name:         string;
  emoji:        string;
  passportPath: string;
  ownership:    WorkspaceOwnership;
  state:        WorkspaceCurrentState;
}

export interface CeoDecision {
  id:       string;
  subject:  string;
  body:     string;
  from:     string;
  priority: Priority;
  date:     string;
}

export interface StudioV1Data {
  lastUpdated: string;
  mission:     string;
  production:  ProductionBanner;
  workspaces:  Workspace[];
  decisions:   CeoDecision[];
}

// ─── Display Data ─────────────────────────────────────────────────────────────

export const STUDIO: StudioV1Data = {
  lastUpdated: '2026-06-30',
  mission:     'Build the best domino game in the world and make it go viral.',

  production: {
    branch:     'master',
    health:     'healthy',
    commit:     'ac33817',
    lastDeploy: '2026-06-30',
  },

  workspaces: [
    // ── 1. React App ─────────────────────────────────────────────────────────
    {
      id:           'frontend',
      name:         'React App Workspace',
      emoji:        '🎨',
      passportPath: 'frontend/studio/workspaces/react-app.md',
      ownership: {
        reactUI:        true,
        backend:        false,
        gameplay:       false,
        studioOs:       false,
        infrastructure: false,
        passportSystem: false,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'SBGDS complete and merged to develop. All screens redesigned with canonical tokens and new branding. Next: integration QA, then develop → master.',
        priority:        'high',
        lastWin:         'SBGDS — Studio Brand & Game Design System — complete (2853a96, 2026-06-30). 61 files, 1,511 insertions. All screens redesigned: Landing, Home, Auth, Friends, Profile, Stats, Lobby, Play Online, Settings, GameScreen shell. Full route QA passed.',
        nextAction:      'Integration QA on develop — full route regression with SBGDS + Gameplay Experience V1 active.',
        blockers:        [],
        pendingDecisions: [],
        branch:            'develop',
        constraints: [
          'Do not touch Phaser or game logic.',
          'Do not touch authentication or backend code.',
          'Do not touch other workspace scopes.',
          'Do not stage dist.zip.',
        ],
        extraVerification: [],
        lastUpdated: '2026-06-30',
      },
    },

    // ── 2. Gameplay / Phaser ─────────────────────────────────────────────────
    {
      id:           'gameplay',
      name:         'Gameplay Engine Workspace',
      emoji:        '🎮',
      passportPath: 'frontend/studio/workspaces/gameplay-engine.md',
      ownership: {
        reactUI:        false,
        backend:        false,
        gameplay:       true,
        studioOs:       false,
        infrastructure: false,
        passportSystem: false,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'Gameplay Experience V1 active. Drag-to-play implemented and merged to develop. Continuing gameplay UX polish — animations, board feel, sounds, multiplayer feel.',
        priority:        'high',
        lastWin:         'Gameplay Experience V1 merged to develop (2d43429, 2026-06-30): drag-to-play tile interaction, drag-release animation source, rack reorder fixed (always-allow, stacking bug, slot calc). Hand.ts, GameScene.ts, Board.ts.',
        nextAction:      'Continue Gameplay Experience V1 — tile animations, board feel, sounds, AI polish, multiplayer polish.',
        blockers:        [],
        pendingDecisions: [
          'Bot difficulty levels — how many tiers and what are the heuristic rules for each?',
          'Packaging format — PWA only, Capacitor wrapper, or React Native shell?',
        ],
        branch:            'feature/gameplay-experience-v1',
        constraints: [
          'Do not touch React UI components outside Phaser canvas.',
          'Do not touch authentication, backend controllers, or ActionCable server.',
          'Do not modify sound files or asset paths without checking project_audio_architecture.md.',
          'Do not commit debug overlays, hitbox graphics, or temporary console.log calls.',
          'Do not own Friends & Social, lobby UI, invitation system, or any React screen.',
        ],
        extraVerification: [],
        lastUpdated: '2026-06-30',
      },
    },

    // ── 3. Platform / Infrastructure ─────────────────────────────────────────
    {
      id:           'platform',
      name:         'Platform Workspace',
      emoji:        '⚙️',
      passportPath: 'frontend/studio/workspaces/platform.md',
      ownership: {
        reactUI:        false,
        backend:        true,
        gameplay:       false,
        studioOs:       false,
        infrastructure: true,
        passportSystem: false,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'Production stable and monitored. Git workflow enforced: feature work branches from develop, merges to master via develop only.',
        priority:        'high',
        lastWin:         'Full production recovery confirmed (2026-06-30): /health 200, online gameplay verified by live users. Git workflow migrated — develop branches created for both repos. Render webhook alive.',
        nextAction:      'Monitor production stability during beta. Coordinate Friends migration fixes before re-merge.',
        blockers:        [],
        pendingDecisions: [],
        branch:            'master',
        constraints: [
          'Do not touch React UI or game logic.',
          'Do not touch Phaser scenes.',
          'No Firestore or Cloud Functions — this project uses Rails + PostgreSQL + ActionCable.',
        ],
        extraVerification: [
          'After auth changes: confirm Render logs show Firebase cert loaded successfully on boot.',
        ],
        lastUpdated: '2026-06-30',
      },
    },

    // ── 4. Friends & Social ──────────────────────────────────────────────────
    {
      id:           'social',
      name:         'Friends & Social Workspace',
      emoji:        '👥',
      passportPath: 'frontend/studio/workspaces/friends-social.md',
      ownership: {
        reactUI:        true,
        backend:        true,
        gameplay:       false,
        studioOs:       false,
        infrastructure: false,
        passportSystem: false,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'Production-ready and stable. Full invitation lifecycle, presence (online/in_game/offline), and real-time toasts all verified. Monitoring beta. Re-merge to master pending migration safety fixes.',
        priority:        'high',
        lastWin:         'Full real-time social system stabilized: incoming + outgoing invitations, GameInvitationToast, SocialToastManager, lobby integration, presence upgrade to online/in_game/offline, browser cache fix. Beta scalability audit complete.',
        nextAction:      'Live-test friend presence, game invites, and lobby join flow with two real users.',
        blockers:        [],
        pendingDecisions: [
          'When concurrency approaches 2,000–5,000 users, replace 60-second polling with push-based presence_changed events.',
        ],
        branch:            'feature/friends-social-v1',
        constraints: [
          'Do not touch gameplay logic or Phaser.',
          'Do not touch authentication or Firebase security rules.',
          'Do not touch any workspace outside Friends & Social scope.',
        ],
        extraVerification: [
          'Run backend migration dry-run if new migrations were added.',
        ],
        lastUpdated: '2026-06-30',
      },
    },

    // ── 5. Studio OS ─────────────────────────────────────────────────────────
    {
      id:           'studio-os',
      name:         'Studio OS Workspace',
      emoji:        '🧭',
      passportPath: 'frontend/studio/workspaces/studio-os.md',
      ownership: {
        reactUI:        false,
        backend:        false,
        gameplay:       false,
        studioOs:       true,
        infrastructure: false,
        passportSystem: true,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'All 7 workspaces synchronized to develop state. SBGDS + Gameplay V1 both recorded. Develop is the active integration branch; master unchanged at ac33817.',
        priority:        'high',
        lastWin:         'SBGDS milestone sync (2026-06-30): React App, Gameplay Engine, Product & UX passports all updated to reflect current develop state. workspaceState.json and agentCommandCenter.ts current.',
        nextAction:      'Sync again after Gameplay Experience V1 completes; coordinate develop → master promotion with Deploy/OPS.',
        blockers:        [],
        pendingDecisions: [],
        branch:            'develop',
        constraints: [
          'Do not touch gameplay, authentication, or backend code.',
          "Do not modify other workspaces' passport files — each workspace owns its own.",
        ],
        extraVerification: [],
        lastUpdated: '2026-06-30',
      },
    },

    // ── 6. Deploy / OPS ──────────────────────────────────────────────────────
    {
      id:           'deploy-ops',
      name:         'Deploy / OPS Workspace',
      emoji:        '🚀',
      passportPath: 'frontend/studio/workspaces/deploy-ops.md',
      ownership: {
        reactUI:        false,
        backend:        false,
        gameplay:       false,
        studioOs:       false,
        infrastructure: true,
        passportSystem: false,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'Maintain production stability and enforce git workflow. All feature work flows through develop before master.',
        priority:        'high',
        lastWin:         'Git workflow migrated to master/develop/feature/hotfix. develop branches created for frontend and backend. Render webhook verified — push-to-master deploys within ~5 minutes.',
        nextAction:      'Monitor production stability; coordinate Friends & Social re-merge through develop when migration fixes are ready.',
        blockers:        [],
        pendingDecisions: [],
        branch:            'develop',
        constraints: [
          'Never push directly to master — all changes flow through develop first.',
          'Hotfixes are the only exception: merge to both master and develop.',
          'Do not modify workspace passport files.',
        ],
        extraVerification: [
          'Run prepush-backend and npm run prepush before any master promotion.',
        ],
        lastUpdated: '2026-06-30',
      },
    },

    // ── 7. Product & UX ──────────────────────────────────────────────────────
    {
      id:           'product-ux',
      name:         'Product & UX Workspace',
      emoji:        '✨',
      passportPath: 'frontend/studio/workspaces/product-ux.md',
      ownership: {
        reactUI:        true,
        backend:        false,
        gameplay:       false,
        studioOs:       false,
        infrastructure: false,
        passportSystem: false,
      },
      state: {
        health:          'healthy',
        knowledgeStatus: 'in_sync',
        lastSync:        '2026-06-30',
        currentMission:  'SBGDS complete. Active sprint: Gameplay Experience V1 — drag & drop polish, animations, board feel, sounds, AI polish, multiplayer feel. Next: Integration QA → Release Candidate → Closed Beta.',
        priority:        'high',
        lastWin:         'SBGDS — largest frontend milestone to date — complete and merged to develop. All screens redesigned, canonical design tokens, new branding, full route QA passed.',
        nextAction:      'Drive Gameplay Experience V1 sprint; plan Integration QA and develop → master promotion criteria.',
        blockers:        [],
        pendingDecisions: [
          'Friends & Social backend re-merge: when are migration fixes ready?',
          'Closed Beta invitations: target date and invite list.',
        ],
        branch:            'develop',
        constraints: [
          'Do not implement features without CEO design approval.',
          'Do not touch Phaser canvas or gameplay logic.',
          'Do not touch backend API or authentication.',
        ],
        extraVerification: [],
        lastUpdated: '2026-06-30',
      },
    },
  ],

  decisions: [],
};
